import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { type PopupShowOptions, usePopupAnalytics } from '@/composables/use-popup-analytics'
import { useLayoutStore } from '@/store/layout-store'
import { type NotificationName, useNotificationsQueue } from '@/store/notification-que/notifications-queue'

export interface ToastOptions extends PopupShowOptions {
    type: NotificationName
    title: string
    description: string
    imageStyles?: Record<string, string>
    animation?: Record<string, unknown>
    isFullScreenAllowed?: boolean
    image?: string
    buttonText?: string
    weight: number
    showTimeoutMs?: number
    onAccept?: () => void
    onClose?: () => void
    onShow?: () => void
}

export const useToastStore = defineStore('toast', () => {
    const isVisible = ref(false)
    const toast = ref<ToastOptions | null>(null)
    const notificationsQueue = useNotificationsQueue()
    const layoutStore = useLayoutStore()

    const isToastVisible = computed(
        () => isVisible.value && (!layoutStore.fullScreen || !!toast.value?.isFullScreenAllowed),
    )

    let closeReason: string | null = null
    let shownToast: ToastOptions | null = null

    watch(
        () => (isToastVisible.value ? toast.value : null),
        (visibleToast) => {
            if (visibleToast && visibleToast !== shownToast) {
                shownToast = visibleToast
                usePopupAnalytics(visibleToast.type).shown(visibleToast)
            }
        },
    )

    const showToast = (options: ToastOptions) => {
        const popupAnalytics = usePopupAnalytics(options.type)
        notificationsQueue.request({
            name: options.type,
            weight: options.weight,
            onOpen: () => {
                toast.value = { ...options }
                isVisible.value = true
                closeReason = null
                options.onShow?.()
            },
            onClose: (reason) => {
                if (toast.value?.type === options.type && shownToast === toast.value && closeReason !== 'accept') {
                    popupAnalytics.closed(closeReason || reason)
                }
                isVisible.value = false
                toast.value = null
            },
        })
    }

    const closeToast = (reason: 'close-button' | 'timeout' | 'replaced') => {
        if (toast.value) {
            closeReason = reason
            notificationsQueue.remove(toast.value.type)
        }
    }

    const acceptToast = () => {
        if (toast.value) {
            usePopupAnalytics(toast.value.type).clicked('accept')
            closeReason = 'accept'
            toast.value.onAccept?.()
            notificationsQueue.remove(toast.value.type)
        }
    }

    return {
        toast,
        isVisible,
        isToastVisible,
        showToast,
        closeToast,
        acceptToast,
    }
})
